import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Phone, Check, Clock } from "lucide-react";
import confetti from "canvas-confetti";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";

const REIN_PHONE = "8663552580";
const REIN_PHONE_DISPLAY = "(866) 355-2580";

/**
 * Check if current time is within Rein business hours (9am-6pm EST).
 */
const isWithinBusinessHours = () => {
  const now = new Date();
  // Get current hour in EST (America/New_York)
  const estTime = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" })
  );
  const hour = estTime.getHours();
  const day = estTime.getDay(); // 0=Sun, 6=Sat
  // Weekdays 9am-6pm
  return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
};

const ReinCallScreen = ({ buyerData, onComplete, quickConnect = false }) => {
  const { trackButtonClick } = useInteractionTracking();
  const [duringHours, setDuringHours] = useState(isWithinBusinessHours());
  const headerRef = useRef(null);

  // Fire confetti on mount
  useEffect(() => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { x, y },
      colors: ["#7B61FF", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
    });
  }, []);

  useEffect(() => {
    analyticsService.trackPageView("rein_call_screen", {
      buyer: buyerData?.name,
      during_business_hours: duringHours,
    });

    // Auto-open dialer if within business hours
    if (duringHours) {
      const timer = setTimeout(() => {
        openDialer();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const openDialer = () => {
    trackButtonClick("open-dialer", {
      step_name: "rein_call_screen",
      phone: REIN_PHONE,
    });
    window.location.href = `tel:${REIN_PHONE}`;
  };

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center space-y-4" ref={headerRef}>
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-xl font-bold uppercase">
          Offer Accepted.. You're All Set!
        </h1>
        <p className="text-darkGrey">
          We've saved your contact preferences and passed them to{" "}
          <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span>. They
          have your Ownli Insurance Profile, are getting to work, and will be
          in touch within one business day.
        </p>
        {!quickConnect && (
          <p className="text-darkGrey">
            Your reward will be added to your Ownli Wallet once your profile is
            fully verified.
          </p>
        )}
      </div>

      {/* Buyer Logo */}
      {buyerData?.logo && (
        <div className="flex justify-center">
          <div className="bg-gray-50 p-4 rounded-lg">
            <img
              src={buyerData.logo}
              alt={buyerData.name}
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      )}

      {/* Next Step — hours-aware */}
      <Card className="border-2 border-[#7B61FF] bg-white rounded-[20px]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            {duringHours ? (
              <>
                <Phone className="w-12 h-12 text-[#7B61FF] mx-auto" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-2">
                    Next Step: Call {buyerData?.name}
                  </h2>
                  <p className="text-gray-600 text-sm">
                    An expert is ready to help you get your personalized
                    insurance quote
                  </p>
                </div>

                {/* Phone Number Display */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">
                    Call this number:
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {REIN_PHONE_DISPLAY}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Hours: 9am–6pm EST, Mon–Fri
                  </p>
                </div>

                {/* Call Now Button */}
                <button
                  onClick={openDialer}
                  className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </button>

                <p className="text-xs text-gray-500">
                  Tap the button above to dial automatically
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Prefer not to call? No worries — you'll receive a call within 1 business day.
                </p>
              </>
            ) : (
              <>
                <Clock className="w-12 h-12 text-[#7B61FF] mx-auto" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-2">
                    You'll Get a Call Within 1 Business Day
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {buyerData?.name} experts are available 9am–6pm EST,
                    Mon–Fri. They'll reach out to help you with your
                    personalized insurance quote.
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">
                    Or call during business hours:
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {REIN_PHONE_DISPLAY}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    9am–6pm EST, Mon–Fri
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* What to Expect */}
      <Card className="border-gray-200 rounded-[20px]">
        <CardContent className="p-5">
          <h3 className="font-bold text-gray-900 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>
                A {buyerData?.name} expert will review your Ownli Insurance
                Profile
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>They'll provide personalized quote options</span>
            </li>
            {!quickConnect && (
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>
                  Your reward will be approved after profile validation
                </span>
              </li>
            )}
          </ul>
        </CardContent>
      </Card>

      {/* Skip Option */}
      {/* {onComplete && (
        <button
          onClick={onComplete}
          className="w-full text-center text-gray-600 hover:text-gray-900 text-sm"
        >
          I'll call later
        </button>
      )} */}
    </div>
  );
};

export default ReinCallScreen;
